import { Controller } from "@hotwired/stimulus"

// Supports 1–3 cost/price/margin rows. Each row's targets are suffixed: cost1/price1/margin1, etc.
// Falls back to legacy single-row targets (cost/price/margin) for backward compatibility.
export default class extends Controller {
  static targets = [
    "cost", "price", "margin",
    "cost1", "price1", "margin1",
    "cost2", "price2", "margin2",
    "cost3", "price3", "margin3"
  ]

  connect() {
    // Sync all rows on connect
    for (let i = 1; i <= 3; i++) {
      this.syncMarginFromPrice(i)
    }
    // Legacy single-row fallback
    if (this.hasCostTarget) this.syncMarginFromPriceLegacy()
  }

  // --- Row-specific handlers (called from data-action with data-row) ---
  updateFromCostRow(event) {
    const row = this.getRow(event)
    if (this.hasMarginForRow(row) && this.marginForRow(row).value !== "") {
      this.syncPriceFromMargin(row)
    } else {
      this.syncMarginFromPrice(row)
    }
  }

  updateFromMarginRow(event) {
    this.syncPriceFromMargin(this.getRow(event))
  }

  updateFromPriceRow(event) {
    this.syncMarginFromPrice(this.getRow(event))
  }

  // --- Legacy single-row handlers (backward compat) ---
  updateFromCost() {
    if (this.hasMarginTarget && this.marginTarget.value !== "") {
      this.syncPriceFromMarginLegacy()
      return
    }
    this.syncMarginFromPriceLegacy()
  }

  updateFromMargin() {
    this.syncPriceFromMarginLegacy()
  }

  updateFromPrice() {
    this.syncMarginFromPriceLegacy()
  }

  // --- Row-based sync ---
  syncPriceFromMargin(row) {
    if (!this.hasCostForRow(row) || !this.hasPriceForRow(row) || !this.hasMarginForRow(row)) return

    const cost = this.parseNumber(this.costForRow(row).value)
    const margin = this.parseNumber(this.marginForRow(row).value)

    if (cost === null || margin === null || cost <= 0) {
      if (cost === null || cost <= 0) this.marginForRow(row).value = ""
      return
    }

    this.priceForRow(row).value = this.formatNumber(cost * (1 + (margin / 100)))
  }

  syncMarginFromPrice(row) {
    if (!this.hasCostForRow(row) || !this.hasPriceForRow(row) || !this.hasMarginForRow(row)) return

    const cost = this.parseNumber(this.costForRow(row).value)
    const price = this.parseNumber(this.priceForRow(row).value)

    if (cost === null || price === null || cost <= 0) {
      this.marginForRow(row).value = ""
      return
    }

    this.marginForRow(row).value = this.formatNumber(((price - cost) / cost) * 100)
  }

  // --- Legacy single-row sync ---
  syncPriceFromMarginLegacy() {
    if (!this.hasCostTarget || !this.hasPriceTarget || !this.hasMarginTarget) return
    const cost = this.parseNumber(this.costTarget.value)
    const margin = this.parseNumber(this.marginTarget.value)
    if (cost === null || margin === null || cost <= 0) {
      if (cost === null || cost <= 0) this.marginTarget.value = ""
      return
    }
    this.priceTarget.value = this.formatNumber(cost * (1 + (margin / 100)))
  }

  syncMarginFromPriceLegacy() {
    if (!this.hasCostTarget || !this.hasPriceTarget || !this.hasMarginTarget) return
    const cost = this.parseNumber(this.costTarget.value)
    const price = this.parseNumber(this.priceTarget.value)
    if (cost === null || price === null || cost <= 0) {
      this.marginTarget.value = ""
      return
    }
    this.marginTarget.value = this.formatNumber(((price - cost) / cost) * 100)
  }

  // --- Helpers ---
  getRow(event) {
    return parseInt(event.currentTarget.dataset.row || "1", 10)
  }

  costForRow(row) { return this[`cost${row}Target`] }
  priceForRow(row) { return this[`price${row}Target`] }
  marginForRow(row) { return this[`margin${row}Target`] }
  hasCostForRow(row) { return this[`hasCost${row}Target`] }
  hasPriceForRow(row) { return this[`hasPrice${row}Target`] }
  hasMarginForRow(row) { return this[`hasMargin${row}Target`] }

  parseNumber(value) {
    if (value === null || value === undefined || value === "") return null
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  formatNumber(value) {
    return (Math.round((value + Number.EPSILON) * 100) / 100).toString()
  }
}